import { StateContext } from "@ngxs/store";

import { WebApiResponse } from "@shared/classes/general";
import { AppDataStateModel } from "@app/shared/models/app-data.model";
import { KpiITToolUsage } from "@shared/classes/kpi-it-tool-usage";
import { DrivingBehavior } from "@shared/classes/driving-behavior";
import { KpiEconomy } from "@shared/classes/kpi-economy";
import { KpiHelper } from "@app/pages/kpi-data-mgmt/kpi.helper";
import { KpiOthers } from "@shared/classes/kpi-others";
import { Coaching } from "@shared/classes/coaching";
import { UserProfile } from "@shared/classes/user-profile";
import { Utilities } from "@shared/utils/utilities";
import { KpiSafety } from "@shared/classes/kpi-safety";
import { Achievement } from "@shared/classes/achievement";
import { OrgUnit } from "@shared/classes/org-unit";
import { TeamMessageRead } from "@shared/classes/team-message";
import { Driver } from "@shared/classes/driver";
import { KpiConfiguration } from "@shared/classes/kpi-weight-management";
import { Award } from "@shared/classes/award";
import { GroupByHelper } from "@shared/utils/group-by.helper";
import { UserProfileRole } from "@shared/classes/user-profile-role";
import { UserProfileFieldName } from "@app/pages/user-profile/user-profile.constant";
import { BusinessHelper } from "@shared/utils/business-helper";
import { Status } from "@shared/enums";

export class NgxsCallBacks {

  static GetDriverData = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<Driver[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.apexId = item.apexId;
      item.employeeId = item.employeeId;
      item.fullName = item.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.businessType = item.businessType;
      item.employeeOr3PL = item.employeeOr3PL;
      item.status = item.status;
      item.lastModified_On = item.lastModified_On;
    });

    return response;
  }

  static GetOrgUnit = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<OrgUnit[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.subRegionName = item.subRegion?.subRegionName;
    });

    return response;
  }

  static GetUserProfileRole = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<UserProfileRole[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return [];
    }

    return GroupByHelper.sortArray(response.data, UserProfileFieldName.role_Id, true);
  }

  static GetUserProfile = (ctx: StateContext<AppDataStateModel>, response: UserProfile) => {
    if (!response || Utilities.isEmptyString(response.apexId)) {
      return response;
    }

    response.fullName = BusinessHelper.GetFullNameByFirstLastName(response.fullName, response.firstName, response.lastName);
    return response;
  }

  static GetUserProfileData = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<UserProfile[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.fullName = BusinessHelper.GetFullNameByFirstLastName(item.fullName, item.firstName, item.lastName);
      item.roleName = item.role?.roleName;
    });

    return response;
  }

  static GetKpiITToolUsage = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<KpiITToolUsage[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.driverName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.kpiName = item.kpi?.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
      item.occurrenceToTarget =
        ((item.occurrence !== undefined && item.occurrence !== null) || (item.targetOccurrence !== undefined && item.targetOccurrence !== null)) ?
          `${item.occurrence ?? ''}/${item.targetOccurrence ?? ''}` : "";
    });

    return response;
  }

  static GetKpiSafety = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<KpiSafety[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.driverName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.kpiName = item.kpi?.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
      item.status = item.status ?? Status.ACTIVE;
    });

    return response;
  }

  static GetKpiDrivingBehavior = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<DrivingBehavior[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.driverName = item.driver?.fullName;
      item.date = item.kpiTime;
      item.time = item.kpiTime;
      item.kpiName = item.kpi?.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
    });

    return response;
  }

  static GetKpiEconomy = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<KpiEconomy[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.fullName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.kpiName = item.kpi?.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
      item.kpiUom = item.kpi?.uom;
      item.kpiValueWithUom = KpiHelper.GenerateEconomyValueWithUom(item);
    });

    return response;
  }

  static GetKpiOthers = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<KpiOthers[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.apexId = item.driver?.apexId;
      item.driverName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.kpiName = item.kpi?.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
    });

    return response;
  }

  static GetCoaching = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<Coaching[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.driverName = item.driver?.fullName;
      item.driverApexId = item.driver?.apexId;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
      item.attachmentVisibletoDriver = item.attachmentVisibletoDriver ?? false;
      item.driver3PLflag = item.driver3PLflag ?? false;
      item.driver3PLterminal_Id = item.driver3PLterminal_Id;
    });

    return response;
  }

  static GetAchievement = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<Achievement[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.driverName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
    });

    return response;
  }

  static GetAward = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<Award[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.driverName = item.driver?.fullName;
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
    });

    return response;
  }

  static GetTeamMessageRead = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<TeamMessageRead[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
    });

    return response;
  }

  static GetTeamMessageReadForExport = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<TeamMessageRead[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.terminalId = item.terminal?.terminalId;
      item.terminalName = item.terminal?.terminalName;
      item.subRegionName = item.terminal?.subRegion?.subRegionName;
      item.country = item.terminal?.country;
    });

    return response;
  }

  static GetKpiConfiguration = (ctx: StateContext<AppDataStateModel>, response: WebApiResponse<KpiConfiguration[]>) => {
    if (!response || !response.data || response.data.length === 0) {
      return response;
    }

    response.data.forEach(item => {
      item.kpiCategoryName = item.kpiCategory?.kpiCategoryName;
      item.kpiName = item.kpi.kpiName;
      item.kpi_Translation_Id = item.kpi?.kpi_Translation_Id;
      item.kpiCategory_Translation_Id = item.kpiCategory?.kpiCategory_Translation_Id;
      item.categoryWeight = Utilities.Sum(response.data.filter(resItem => resItem.kpiCategory?.kpiCategoryName === item.kpiCategoryName).map(resItem => resItem.weight));
    });

    return response;
  }
}
